import { useCallback, useEffect, useState } from 'react'

import { useT } from '../../i18n'
import { api } from '../../utils/api'
import { formatFarmDateTime } from '../../utils/farmTime'
import { lotApi } from '../../utils/lotApi'

// Findings #197: a lot deleted by mistake (validation day 1: 26SETE1 in R7)
// can be brought back as it was, in its same unit — its records were never
// removed. Office only, online only; the server refuses it when that unit now
// holds an active lot with the same name.

type DeletedLot = { id: string; lotKey: string; deletedAt: string; productionUnitId?: string }

export default function DeletedLotsSection(props: {
  farmId: string
  timeZone: string
  unitName: (productionUnitId: string | undefined) => string
  canEdit: boolean
  onRestored: () => void
}) {
  const t = useT()
  const [lots, setLots] = useState<DeletedLot[] | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  const load = useCallback(() => {
    if (!props.farmId) {
      setLots(null)
      return () => {}
    }
    let cancelled = false
    void api.lots.deleted(props.farmId)
      .then((rows) => { if (!cancelled) setLots(Array.isArray(rows) ? rows : []) })
      .catch(() => { if (!cancelled) setLots(null) })
    return () => { cancelled = true }
  }, [props.farmId])

  useEffect(() => load(), [load])

  if (!props.farmId || lots === null) return null

  const sorted = [...lots].sort((a, b) => Date.parse(b.deletedAt) - Date.parse(a.deletedAt))

  const restore = async (lot: DeletedLot) => {
    const unit = props.unitName(lot.productionUnitId)
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setMessage({ tone: 'error', text: t('lots.deleted.offline') })
      return
    }
    setBusyId(lot.id)
    setMessage(null)
    try {
      await lotApi.restoreRemote(lot.id)
      setMessage({ tone: 'ok', text: t('lots.deleted.restored', { lot: lot.lotKey, unit }) })
      load()
      props.onRestored()
    } catch (error) {
      const reason = error instanceof Error && /already exists/i.test(error.message)
        ? t('lots.move.error.nameTaken', { lot: lot.lotKey })
        : error instanceof Error ? error.message : String(error)
      setMessage({ tone: 'error', text: t('lots.deleted.error', { lot: lot.lotKey, reason }) })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <details style={{ marginTop: 16, fontSize: 13, color: '#334155' }} data-testid="lots-deleted-section">
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{t('lots.deleted.title', { count: String(sorted.length) })}</summary>
      <p style={{ margin: '8px 0', color: '#64748b' }}>{t('lots.deleted.help')}</p>
      {message ? (
        <div role={message.tone === 'error' ? 'alert' : 'status'} style={{ marginBottom: 8, color: message.tone === 'error' ? '#b91c1c' : '#047857' }}>
          {message.text}
        </div>
      ) : null}
      {sorted.length === 0 ? (
        <div style={{ color: '#64748b' }}>{t('lots.deleted.empty')}</div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
          {sorted.map((lot) => (
            <li key={lot.id} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>
                {t('lots.deleted.row', {
                  lot: lot.lotKey,
                  unit: props.unitName(lot.productionUnitId),
                  date: formatFarmDateTime(lot.deletedAt, props.timeZone)
                })}
              </span>
              {props.canEdit ? (
                <button
                  type="button"
                  onClick={() => { void restore(lot) }}
                  disabled={busyId !== null}
                  style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5f5', background: '#fff', cursor: 'pointer' }}
                  data-testid={`lots-deleted-restore-${lot.id}`}
                >
                  {busyId === lot.id ? t('lots.deleted.restoring') : t('lots.deleted.restore')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </details>
  )
}
